import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import swal from 'sweetalert';
import axiosInstance from '../../../redux/api';
import { getUser } from '../../../redux/user/action';
import { handleImageUpload } from '../../Common/ImageUpload/handleImageUpload';
import { Button } from 'Components/ui/button';
import { Input } from 'Components/ui/input';
import { Label } from 'Components/ui/label';
import {
  PageShell,
  PageHeader,
  PageHeaderRow,
  PageTitle,
  PageDescription,
  PageBody,
} from 'Components/ui/page-shell';
import { cn } from 'lib/utils';
import PushLinkPicker from './PushLinkPicker';
import Segmented from './Segmented';
import { formatDateTime } from '../Order/shared/orderFormat';
import { toScheduledIso } from './pushHistoryView';
import SchedulePicker from './SchedulePicker';
import { slotAllowed, splitValue } from './scheduleView';
import { flatSelectStyles } from './flatSelectStyles';
import { PLACEHOLDER, PUSH_TEMPLATES } from './pushTemplates';

const customerTypeOptions = [
  { label: 'One customer', value: 'individual', apiValue: 0, type: 0 },
  { label: 'Several customers', value: 'multi', apiValue: 0, type: 2 },
  { label: 'Everyone', value: 'all', apiValue: 1, type: 1 },
];

const TEMPLATE_LANGUAGES = [
  { value: 'ar', label: 'العربية' },
  { value: 'en', label: 'English' },
];

const WHEN_OPTIONS = [
  { value: 'now', label: 'Send now' },
  { value: 'later', label: 'Schedule for later' },
];

// Roughly what most phones show before cutting the text off.
const TITLE_GUIDE = 50;
const MESSAGE_GUIDE = 150;

// Flat fields: the kit's inputs carry a drop shadow and the page's grey.
const FIELD = 'h-10 bg-white shadow-none';
const TEXTAREA = cn(
  'flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm transition-colors',
  'placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

// The server's own reason when it refuses to send.
const serverMessage = (error, fallback) => error?.response?.data?.message || fallback;

// "Noura Alharbi · noura@x.com": the name when there is one, then how to reach them.
const customerLabel = (u) => {
  const name = (u.full_name || '').replace(/\bNone\b/g, '').trim();
  const contact = u.email_id || [u.country_code, u.mobile_number].filter(Boolean).join(' ');
  return [name, contact].filter(Boolean).join(' · ') || `#${u.id}`;
};

const noBlanks = (text) => !PLACEHOLDER.test(text || '');

const Section = ({ title, hint, action, children }) => (
  <section className='space-y-4 border-b px-6 py-6 last:border-b-0'>
    <div className='flex items-start justify-between gap-4'>
      <div className='space-y-1'>
        <h2 className='text-[15px] font-semibold text-foreground'>{title}</h2>
        {hint && <p className='text-sm text-muted-foreground'>{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Field = ({ label, htmlFor, hint, error, children }) => (
  <div className='space-y-1.5'>
    {label && <Label htmlFor={htmlFor}>{label}</Label>}
    {children}
    {error ? (
      <p className='text-xs text-destructive'>{error}</p>
    ) : (
      hint && <p className='text-xs text-muted-foreground'>{hint}</p>
    )}
  </div>
);

const counter = (text, guide) => {
  const length = text.length;
  return length > guide ? `${length}/${guide} · may be cut off on the phone` : `${length}/${guide}`;
};

const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

// Arabic reads right to left: set the direction so punctuation lands on the correct side.
const textDir = (text) => (/[؀-ۿ]/.test(text || '') ? 'rtl' : 'auto');

/** The notification as an iPhone lock screen shows it: picture on the left, title, message. */
const Preview = ({ title, message, image }) => (
  <div className='space-y-3'>
    <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Preview</p>
    <div className='rounded-[28px] border bg-[#eceef1] px-4 pb-8 pt-7'>
      <p className='text-center text-[40px] font-light leading-none text-foreground/80'>9:41</p>
      <p className='mb-6 mt-1.5 text-center text-xs text-foreground/60'>{today}</p>
      <div className='flex gap-3 rounded-2xl border border-white/70 bg-white/90 p-3'>
        {image ? (
          <img src={image} alt='' className='h-[38px] w-[38px] shrink-0 rounded-[9px] object-cover' />
        ) : (
          <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] bg-[#111] text-sm font-semibold text-[#c9a96e]'>
            N
          </div>
        )}
        <div className='min-w-0 flex-1'>
          <div className='flex items-baseline justify-between gap-2'>
            <p dir={textDir(title)} className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground', textDir(title) === 'rtl' ? 'text-right' : 'text-left')}>
              {title || 'Title'}
            </p>
            <span className='shrink-0 text-xs text-muted-foreground'>now</span>
          </div>
          <p dir={textDir(message)} className={cn('line-clamp-4 whitespace-pre-line break-words text-[13px] leading-snug text-foreground/80', textDir(message) === 'rtl' ? 'text-right' : 'text-left')}>
            {message || 'Your message'}
          </p>
        </div>
      </div>
    </div>
    <p className='text-xs text-muted-foreground'>How it looks on an iPhone. Long text is cut off.</p>
  </div>
);

const PushNotification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userlist = useSelector(({ user: { userlist } }) => userlist);
  const fileInput = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState(customerTypeOptions[0]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [templateLang, setTemplateLang] = useState('ar');
  const [templateId, setTemplateId] = useState(null);
  const [when, setWhen] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => {
    dispatch(getUser({ page: '', all_customers: selectedType.apiValue }));
  }, [dispatch, selectedType]);

  useEffect(() => {
    if (userlist?.users?.length > 0) {
      setCustomerOptions(userlist.users.map((u) => ({ label: customerLabel(u), value: u.id })));
    }
  }, [userlist]);

  const formik = useFormik({
    initialValues: {
      subject: '',
      description: '',
      image: '',
      customer_ids: [],
      link_type: '',
      link_item: null,
    },
    validationSchema: Yup.object({
      subject: Yup.string().trim().required('Title is required')
        .test('blanks', 'Replace the [brackets] with your text', noBlanks),
      description: Yup.string().trim().required('Message is required')
        .test('blanks', 'Replace the [brackets] with your text', noBlanks),
      link_item: Yup.object()
        .nullable()
        .when('link_type', {
          is: (type) => Boolean(type),
          then: (schema) => schema.required('Choose what a tap opens, or pick "Just open the app"'),
        }),
    }),
    onSubmit: async (values) => {
      const toAll = selectedType.apiValue === 1;
      const chosen = values.customer_ids.length;
      if (!toAll && chosen === 0) {
        swal('Choose customers', 'Pick at least one customer, or choose Everyone.', 'warning');
        return;
      }
      const scheduling = when === 'later';
      if (scheduling && !scheduledAt) {
        swal('Pick a time', 'Choose the date and time to send it (Riyadh time).', 'warning');
        return;
      }
      if (scheduling && !slotAllowed(splitValue(scheduledAt).date, splitValue(scheduledAt).time)) {
        swal('Pick a later time', 'That time has already passed. Pick one at least a few minutes from now.', 'warning');
        return;
      }
      const scheduledIso = scheduling ? toScheduledIso(scheduledAt) : '';
      const whenText = scheduling ? ` on ${formatDateTime(scheduledIso)}` : '';
      const who = toAll
        ? `It goes to every customer who turned on Offers & news${whenText}.`
        : `It goes to the ${chosen} chosen customer(s) who turned on Offers & news${whenText}.`;
      const confirmed = await swal({
        title: scheduling ? 'Schedule this notification?' : 'Send this notification?',
        text: `${who} ${scheduling ? 'You can cancel it from the history until then.' : 'It can’t be taken back.'}`,
        icon: 'warning',
        buttons: ['Cancel', scheduling ? 'Schedule' : 'Send'],
      });
      if (!confirmed) return;

      setLoading(true);
      try {
        const response = await axiosInstance.post('admin/customers/push-notification', {
          subject: values.subject,
          description: values.description,
          image: values.image || '',
          link_type: values.link_item ? values.link_type : '',
          link_id: values.link_item ? values.link_item.value : null,
          type: 1,
          all_customers: selectedType.apiValue,
          customer_ids: toAll ? [] : values.customer_ids.map((c) => c.value),
          scheduled_for: scheduledIso,
        });
        swal(
          scheduling ? 'Scheduled' : 'Sent',
          response?.data?.response?.message || (scheduling ? 'Notification scheduled' : 'Notification sent'),
          'success',
        );
        formik.resetForm();
        setSelectedType(customerTypeOptions[0]);
        setTemplateId(null);
        setWhen('now');
        setScheduledAt('');
      } catch (error) {
        swal(
          scheduling ? 'Not scheduled' : 'Not sent',
          serverMessage(error, 'Something went wrong while sending the notification.'),
          'error',
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors } = formik;

  /** Whether the title and message are exactly the template's text in ``lang``. */
  const isUntouched = (template, lang) =>
    values.subject === template[lang].title && values.description === template[lang].body;

  const fillFrom = (template, lang) => {
    formik.setFieldValue('subject', template[lang].title);
    formik.setFieldValue('description', template[lang].body);
  };

  const applyTemplate = async (template) => {
    const current = PUSH_TEMPLATES.find((t) => t.id === templateId);
    const wroteSomething =
      (values.subject.trim() || values.description.trim()) && !(current && isUntouched(current, templateLang));
    if (wroteSomething) {
      const replace = await swal({
        title: 'Replace what you wrote?',
        text: `The title and message will be replaced with the “${template.label.en}” template.`,
        buttons: ['Keep mine', 'Replace'],
      });
      if (!replace) return;
    }
    fillFrom(template, templateLang);
    // Suggest the usual tap target, but never over a choice already made.
    if (template.link && !values.link_type) {
      formik.setFieldValue('link_type', template.link);
      formik.setFieldValue('link_item', null);
    }
    setTemplateId(template.id);
  };

  const switchTemplateLang = ({ value }) => {
    const current = PUSH_TEMPLATES.find((t) => t.id === templateId);
    // An untouched template follows the language; edited text is left alone.
    if (current && isUntouched(current, templateLang)) {
      fillFrom(current, value);
    }
    setTemplateLang(value);
  };

  const pickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await handleImageUpload(file, { title: 'images', name: 'image' }, 'image', formik);
    } catch {
      // handleImageUpload has already told the admin what went wrong.
    } finally {
      setUploading(false);
    }
  };

  const busy = loading || uploading;
  const toAll = selectedType.type === 1;
  const chosen = values.customer_ids.length;
  const later = when === 'later';
  const verb = later ? 'Schedule' : 'Send';
  const sendLabel = loading
    ? later ? 'Scheduling…' : 'Sending…'
    : toAll
    ? `${verb} for everyone`
    : chosen > 0
    ? `${verb} for ${chosen} customer${chosen === 1 ? '' : 's'}`
    : verb;
  const whenText = later && scheduledAt ? ` on ${formatDateTime(toScheduledIso(scheduledAt))}` : '';
  const audience = toAll
    ? `Goes to every customer who turned on Offers & news${whenText}.`
    : chosen > 0
    ? `Goes to ${chosen} chosen customer${chosen === 1 ? '' : 's'} who turned on Offers & news${whenText}.`
    : 'Choose who gets it.';
  // With no picture attached, the server sends the chosen product's own
  // picture; show that one so the preview matches what customers get.
  const productPicture = values.link_type === 'product' ? values.link_item?.data?.image || '' : '';
  const shownImage = values.image || productPicture;

  return (
    <PageShell>
      <PageHeader>
        <PageHeaderRow>
          <div>
            <PageTitle>Push notification</PageTitle>
            <PageDescription>
              Goes only to customers who turned on Offers &amp; news in the app (Account → Notifications).
              Order updates are sent automatically and aren’t affected.
            </PageDescription>
          </div>
          <Button type='button' variant='outline' className='shadow-none' onClick={() => navigate('/notification/history')}>
            History
          </Button>
        </PageHeaderRow>
      </PageHeader>

      <PageBody className='pb-0 pt-6'>
        <div className='grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_340px]'>
          <form id='push-form' onSubmit={formik.handleSubmit} noValidate className='min-w-0'>
            <div className='overflow-hidden rounded-xl border bg-white'>
              <Section
                title='Start from a template'
                hint='Fills the title and message. Replace anything in [brackets] before sending.'
                action={
                  <Segmented
                    label='Template language'
                    options={TEMPLATE_LANGUAGES}
                    value={templateLang}
                    onChange={switchTemplateLang}
                    disabled={busy}
                  />
                }
              >
                <div className='flex flex-wrap gap-2'>
                  {PUSH_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type='button'
                      disabled={busy}
                      onClick={() => applyTemplate(template)}
                      className={cn(
                        'appearance-none rounded-full border-[1px] border-solid px-3.5 py-1.5 text-sm transition-colors disabled:opacity-50',
                        template.id === templateId
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border bg-white text-foreground/80 hover:border-foreground/40 hover:text-foreground',
                      )}
                    >
                      {template.label.en}
                    </button>
                  ))}
                </div>
              </Section>

              <Section title='Message'>
                <Field
                  label='Title'
                  htmlFor='push-title'
                  hint={counter(values.subject, TITLE_GUIDE)}
                  error={touched.subject && errors.subject}
                >
                  <Input
                    id='push-title'
                    name='subject'
                    dir={textDir(values.subject)}
                    placeholder='e.g. New arrivals from Byredo'
                    className={FIELD}
                    value={values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={busy}
                  />
                </Field>
                <Field
                  label='Message'
                  htmlFor='push-message'
                  hint={counter(values.description, MESSAGE_GUIDE)}
                  error={touched.description && errors.description}
                >
                  <textarea
                    id='push-message'
                    name='description'
                    rows={3}
                    dir={textDir(values.description)}
                    placeholder='What should customers know?'
                    className={TEXTAREA}
                    value={values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={busy}
                  />
                </Field>
              </Section>

              <Section title='When tapped'>
                <PushLinkPicker
                  linkType={values.link_type}
                  linkItem={values.link_item}
                  disabled={busy}
                  error={touched.link_item && errors.link_item}
                  onTypeChange={(type) => {
                    formik.setFieldValue('link_type', type);
                    formik.setFieldValue('link_item', null);
                  }}
                  onItemChange={(item) => formik.setFieldValue('link_item', item)}
                />
              </Section>

              <Section title='Who gets it'>
                <Segmented
                  label='Who gets it'
                  options={customerTypeOptions}
                  value={selectedType.value}
                  disabled={busy}
                  onChange={(option) => {
                    setSelectedType(option);
                    formik.setFieldValue('customer_ids', []);
                  }}
                />
                {toAll ? (
                  <p className='text-sm text-muted-foreground'>Every customer who turned on Offers &amp; news.</p>
                ) : (
                  <Field hint='Only customers who turned on Offers & news receive it.'>
                    <Select
                      // A new mode is a new choice: remount so a single pick never becomes a list.
                      key={selectedType.value}
                      inputId='push-customers'
                      styles={flatSelectStyles}
                      options={customerOptions}
                      isMulti={selectedType.type === 2}
                      isDisabled={busy}
                      placeholder='Search by name, email or phone…'
                      value={values.customer_ids}
                      onChange={(value) => {
                        const selected = Array.isArray(value) ? value : value ? [value] : [];
                        formik.setFieldValue('customer_ids', selected);
                      }}
                    />
                  </Field>
                )}
              </Section>

              <Section title='When' hint='Riyadh time. A scheduled notification can be cancelled from the history until it goes.'>
                <Segmented
                  label='When'
                  options={WHEN_OPTIONS}
                  value={when}
                  disabled={busy}
                  onChange={(option) => setWhen(option.value)}
                />
                {later && <SchedulePicker value={scheduledAt} onChange={setScheduledAt} disabled={busy} />}
              </Section>

              <Section title='Picture' hint='Optional. Shows next to the notification; a square picture works best.'>
                <input ref={fileInput} type='file' accept='image/*' className='hidden' onChange={pickImage} />
                {values.image ? (
                  <div className='flex items-center gap-4'>
                    <img src={values.image} alt='' className='h-16 w-16 rounded-lg border object-cover' />
                    <div className='flex gap-2'>
                      <Button type='button' variant='outline' size='sm' disabled={busy} className='shadow-none' onClick={() => fileInput.current?.click()}>
                        Replace
                      </Button>
                      <Button type='button' variant='ghost' size='sm' disabled={busy} onClick={() => formik.setFieldValue('image', '')}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : productPicture ? (
                  <div className='flex items-center gap-4'>
                    <img src={productPicture} alt='' className='h-16 w-16 rounded-lg border object-cover' />
                    <div className='space-y-1.5'>
                      <p className='text-sm text-foreground'>The product’s picture is used.</p>
                      <Button type='button' variant='outline' size='sm' disabled={busy} className='shadow-none' onClick={() => fileInput.current?.click()}>
                        {uploading ? 'Uploading…' : 'Use a different picture'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Field hint='Up to 5 MB. If a tap opens a product, its picture is used automatically.'>
                    <div>
                      <Button type='button' variant='outline' size='sm' disabled={busy} className='shadow-none' onClick={() => fileInput.current?.click()}>
                        {uploading ? 'Uploading…' : 'Add picture'}
                      </Button>
                    </div>
                  </Field>
                )}
              </Section>
            </div>
          </form>

          <aside className='lg:sticky lg:top-0 lg:self-start'>
            <Preview title={values.subject} message={values.description} image={shownImage} />
          </aside>
        </div>

        <div className='sticky bottom-0 z-10 -mx-6 mt-8 border-t bg-white/95 px-6 py-3 backdrop-blur'>
          <div className='flex max-w-6xl items-center justify-between gap-4'>
            <p className='text-sm text-muted-foreground'>{audience}</p>
            {/* Dark, not bg-primary: Bootstrap's .bg-primary (!important) would paint it bright blue. */}
            <Button
              type='submit'
              form='push-form'
              disabled={busy}
              className='h-10 border-0 bg-foreground px-5 text-background shadow-none hover:bg-foreground/90'
            >
              {sendLabel}
            </Button>
          </div>
        </div>
      </PageBody>
    </PageShell>
  );
};

export default PushNotification;
